/**
 * Share Functionality Module
 * Handles sharing ocean conditions via Web Share API and fallback methods
 */

import { AppState, getCurrentLocationName, getCurrentRegionId, getCurrentAreaId } from './state.js';
import { generateShareUrl } from './router.js';
import { analytics } from './analytics.js';
import { UI_TIMING } from './time.js';
import { logger } from './logger.js';
import { createElement, createSvg } from './dom-utils.js';

// Share panel state (declared early for use in updateShareContent)
let isSharePanelOpen = false;

// Cached conditions for sharing - ensures share always has fresh data even on rapid clicks
let cachedConditions = null;

/**
 * Get current rating info from the UI
 * @returns {{ score: number|null, category: string|null }}
 */
function getCurrentRatingInfo() {
    const category = document.getElementById('rating-text')?.textContent || null;
    const scoreText = document.getElementById('breakdown-score-value')?.textContent;
    const score = scoreText && scoreText !== '--' ? parseInt(scoreText, 10) : null;
    return { score, category };
}

/**
 * Get current conditions data from the UI
 * Returns a consistent data object used for share text and preview
 * @returns {Object} Conditions data
 */
function getCurrentConditions() {
    return {
        locationName: getCurrentLocationName(),
        rating: document.getElementById('rating-text')?.textContent || null,
        score: getCurrentRatingInfo().score,
        swell: document.getElementById('detail-swell')?.textContent || '--',
        wind: document.getElementById('detail-wind')?.textContent || '--',
        tide: document.getElementById('detail-tide')?.textContent || '--',
        water: document.getElementById('detail-water-temp')?.textContent || '--',
        beachAccess: document.getElementById('beach-status')?.textContent || '--',
        stationId: AppState.currentStationId,
        regionId: getCurrentRegionId(),
        areaId: getCurrentAreaId()
    };
}

/**
 * Cache current conditions for sharing
 * Called after data loads to ensure share always has fresh data
 */
function cacheCurrentConditions() {
    cachedConditions = getCurrentConditions();
}

/**
 * Get conditions for sharing (uses cache if available and current)
 * Falls back to reading from DOM if cache is stale
 * @returns {Object} Conditions data
 */
function getShareConditions() {
    // Use cache if it exists and matches current station
    if (cachedConditions && cachedConditions.stationId === AppState.currentStationId) {
        return cachedConditions;
    }
    // Fallback to reading from DOM
    return getCurrentConditions();
}

/**
 * Update the share button text with the current location name
 * Called when location changes to show what will be shared
 */
export function updateShareButtonText() {
    const btn = document.getElementById('share-btn');
    const textSpan = btn?.querySelector('.share-btn-text');
    if (textSpan) {
        const locationName = getCurrentLocationName();
        textSpan.textContent = `Share ${locationName}`;
    }
}

/**
 * Update all share-related content after data loads
 * Called after refreshAllData() to ensure share content is current
 * Updates button text, aria labels, panel title, and caches conditions
 */
export function updateShareContent() {
    // Cache conditions first - ensures share always has fresh data
    cacheCurrentConditions();

    const conditions = cachedConditions;

    // Update button text
    updateShareButtonText();

    // Update share button aria-label with location context
    const shareBtn = document.getElementById('share-btn');
    if (shareBtn) {
        const ariaLabel = conditions.rating
            ? `Share ${conditions.locationName} ocean conditions - ${conditions.rating}`
            : `Share ${conditions.locationName} ocean conditions`;
        shareBtn.setAttribute('aria-label', ariaLabel);
    }

    // Update share panel title
    const panelTitle = document.getElementById('share-title');
    if (panelTitle) {
        panelTitle.textContent = `Share ${conditions.locationName}`;
    }

    // ALWAYS update preview so it's ready when user opens panel
    // This ensures share preview has current data even on first click
    updateSharePreview();
}

// Share targets for fallback UI
// Best practices for each platform as of 2024/2025
const SHARE_TARGETS = {
    twitter: {
        name: 'X',
        // X/Twitter: text param for message, url param for link card
        buildUrl: (text, url) =>
            `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`
    },
    facebook: {
        name: 'Facebook',
        // Facebook: u param for URL (generates preview from OG tags), quote for user text
        buildUrl: (text, url) =>
            `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`
    },
    linkedin: {
        name: 'LinkedIn',
        // LinkedIn: url param only (text comes from OG tags)
        buildUrl: (text, url) =>
            `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`
    },
    reddit: {
        name: 'Reddit',
        // Reddit: url and title params
        buildUrl: (text, url, title) =>
            `https://www.reddit.com/submit?url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}`
    },
    whatsapp: {
        name: 'WhatsApp',
        // WhatsApp: single text param with message + URL
        buildUrl: (text, url) =>
            `https://wa.me/?text=${encodeURIComponent(text + '\n\n' + url)}`
    },
    telegram: {
        name: 'Telegram',
        // Telegram: url and text params
        buildUrl: (text, url) =>
            `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`
    },
    email: {
        name: 'Email',
        // Email: subject and body params
        buildUrl: (text, url, title) =>
            `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(text + '\n\n' + url)}`
    },
    sms: {
        name: 'Message',
        // SMS: body param with full message (URL detection varies by device)
        buildUrl: (text, url) =>
            `sms:?body=${encodeURIComponent(text + '\n\n' + url)}`
    }
};

/**
 * Build share text from cached conditions data
 * Uses cached data to ensure consistency and avoid DOM timing issues
 * @returns {string} Formatted share text
 */
export function buildShareText() {
    const conditions = getShareConditions();

    const ratingDisplay = conditions.rating || 'UNKNOWN';
    const scoreDisplay = conditions.score !== null ? conditions.score : '--';

    const lines = [
        `${conditions.locationName} - Ocean Conditions: ${ratingDisplay} (${scoreDisplay}/100)`,
        `Swell: ${conditions.swell} | Wind: ${conditions.wind} | Tide: ${conditions.tide}`,
        `Water: ${conditions.water} | Beach Access: ${conditions.beachAccess}`
    ];

    return lines.join('\n');
}

/**
 * Get the current shareable URL
 * @returns {string} Full URL string
 */
export function getShareUrl() {
    return generateShareUrl(
        getCurrentRegionId(),
        getCurrentAreaId(),
        AppState.currentStationId
    );
}

/**
 * Get share title for email subject and native share
 * @returns {string} Share title
 */
export function getShareTitle() {
    const locationName = getCurrentLocationName();
    const rating = document.getElementById('rating-text')?.textContent || '';
    return `${locationName} Surf Report - ${rating}`;
}

/**
 * Check if Web Share API is available
 * @returns {boolean} True if navigator.share is supported
 */
export function canUseNativeShare() {
    return typeof navigator.share === 'function';
}

/**
 * Trigger native share using Web Share API
 * @returns {Promise<boolean>} True if share was successful
 */
export async function triggerNativeShare() {
    const title = getShareTitle();
    const text = buildShareText();
    const url = getShareUrl();

    // Include URL in text for iMessage (which ignores separate url param)
    // Also include url param for apps that use it for link previews
    const combinedText = `${text}\n\n${url}`;

    const shareData = {
        title: title,
        text: combinedText,
        url: url  // Keep for Facebook/Twitter link previews
    };

    try {
        await navigator.share(shareData);
        const { score, category } = getCurrentRatingInfo();
        analytics.share('native', 'success', score, category);
        return true;
    } catch (err) {
        // AbortError means user cancelled - not an error
        if (err.name !== 'AbortError') {
            logger.error('Share failed:', err);
            const { score, category } = getCurrentRatingInfo();
            analytics.share('native', 'error', score, category);
        }
        return false;
    }
}

/**
 * Open share target in new window
 * @param {string} targetId - Share target ID (twitter, facebook, etc.)
 */
export function shareToTarget(targetId) {
    const target = SHARE_TARGETS[targetId];
    if (!target) return;

    const text = buildShareText();
    const url = getShareUrl();
    const title = getShareTitle();
    const shareUrl = target.buildUrl(text, url, title);

    window.open(shareUrl, '_blank', 'width=600,height=400,noopener,noreferrer');
    const { score, category } = getCurrentRatingInfo();
    analytics.share(targetId, 'success', score, category);
}

/**
 * Copy share content to clipboard
 * @returns {Promise<boolean>} True if copy was successful
 */
export async function copyToClipboard() {
    const text = buildShareText();
    const url = getShareUrl();
    const fullText = `${text}\n\n${url}`;

    try {
        await navigator.clipboard.writeText(fullText);
        const { score, category } = getCurrentRatingInfo();
        analytics.share('clipboard', 'success', score, category);
        return true;
    } catch (err) {
        logger.error('Copy failed:', err);
        const { score, category } = getCurrentRatingInfo();
        analytics.share('clipboard', 'error', score, category);
        return false;
    }
}

/**
 * Copy just the URL to clipboard
 * @returns {Promise<boolean>} True if copy was successful
 */
export async function copyUrlToClipboard() {
    const url = getShareUrl();

    try {
        await navigator.clipboard.writeText(url);
        const { score, category } = getCurrentRatingInfo();
        analytics.share('clipboard_url', 'success', score, category);
        return true;
    } catch (err) {
        logger.error('Copy URL failed:', err);
        const { score, category } = getCurrentRatingInfo();
        analytics.share('clipboard_url', 'error', score, category);
        return false;
    }
}

/**
 * Update the share preview text in the panel
 * Populates the preview card with current conditions
 */
function updateSharePreview() {
    const conditions = getCurrentConditions();

    // Update location name in preview
    const locationEl = document.getElementById('share-location-name');
    if (locationEl) {
        locationEl.textContent = conditions.locationName;
    }

    // Update rating badge
    const ratingBadge = document.getElementById('share-rating-badge');
    if (ratingBadge && conditions.rating) {
        // Remove existing rating classes
        ratingBadge.classList.remove('good', 'fair', 'poor', 'hazardous');
        // Add new rating class
        ratingBadge.classList.add(conditions.rating.toLowerCase());
    }

    // Update rating text
    const ratingTextEl = document.getElementById('share-rating-text');
    if (ratingTextEl) {
        ratingTextEl.textContent = conditions.rating || '--';
    }

    // Update score
    const scoreValueEl = document.querySelector('#share-score .share-score-value');
    if (scoreValueEl) {
        scoreValueEl.textContent = conditions.score !== null ? conditions.score : '--';
    }

    // Update condition values
    const conditionMap = {
        'share-swell': conditions.swell,
        'share-wind': conditions.wind,
        'share-tide': conditions.tide,
        'share-water': conditions.water
    };

    for (const [id, value] of Object.entries(conditionMap)) {
        const el = document.getElementById(id);
        if (el) el.textContent = value;
    }

    // Update URL preview
    const urlEl = document.getElementById('share-preview-url');
    if (urlEl) {
        urlEl.textContent = getShareUrl();
    }

    // Keep legacy text preview for backwards compatibility (hidden in new UI)
    const previewEl = document.getElementById('share-preview-text');
    if (previewEl) {
        previewEl.textContent = buildShareText();
    }
}

/**
 * Animate share targets with staggered entry
 * Uses CSS custom property for animation delay instead of inline style
 */
function animateShareTargets() {
    const targets = document.querySelectorAll('.share-target');
    targets.forEach((target, index) => {
        target.style.setProperty('--stagger-index', index);
        target.classList.add('animate-in');
    });
}

/**
 * Reset share target animations
 */
function resetShareTargetAnimations() {
    const targets = document.querySelectorAll('.share-target');
    targets.forEach(target => {
        target.classList.remove('animate-in');
        target.style.removeProperty('--stagger-index');
    });
}

/**
 * Open the share panel (fallback UI for desktop)
 */
export function openSharePanel() {
    const panel = document.getElementById('share-panel');
    if (!panel) return;

    panel.hidden = false;
    isSharePanelOpen = true;
    document.body.classList.add('no-scroll');

    // Update share preview with current conditions
    updateSharePreview();

    // Animate share targets with staggered entry
    animateShareTargets();

    const { score, category } = getCurrentRatingInfo();
    analytics.share('panel_open', 'view', score, category);
}

/**
 * Close the share panel
 */
export function closeSharePanel() {
    const panel = document.getElementById('share-panel');
    if (!panel) return;

    panel.hidden = true;
    isSharePanelOpen = false;
    document.body.classList.remove('no-scroll');

    // Reset animations for next open
    resetShareTargetAnimations();
}

/**
 * Handle share button click
 * Uses native share on supported devices, fallback panel otherwise
 */
export async function handleShareClick() {
    if (canUseNativeShare()) {
        await triggerNativeShare();
    } else {
        openSharePanel();
    }
}

/**
 * Show copy feedback toast
 * @param {boolean} success - Whether the copy was successful
 */
function showCopyFeedback(success) {
    const toast = document.getElementById('copy-toast');
    if (!toast) return;

    toast.textContent = success ? 'Copied to clipboard!' : 'Copy failed';
    toast.classList.add('show');
    toast.classList.toggle('error', !success);

    setTimeout(() => {
        toast.classList.remove('show', 'error');
    }, UI_TIMING.toastDuration);
}

// SVG icon definitions for share targets
const SHARE_ICONS = {
    close: {
        width: 14, height: 14, viewBox: '0 0 24 24',
        attrs: { fill: 'none', stroke: 'currentColor', 'stroke-width': '2' },
        children: [
            { tag: 'line', x1: '18', y1: '6', x2: '6', y2: '18' },
            { tag: 'line', x1: '6', y1: '6', x2: '18', y2: '18' }
        ]
    },
    location: {
        width: 14, height: 14, viewBox: '0 0 24 24',
        attrs: { fill: 'none', stroke: 'currentColor', 'stroke-width': '2' },
        children: [
            { d: 'M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z' },
            { tag: 'circle', cx: '12', cy: '10', r: '3' }
        ]
    },
    sms: {
        width: 20, height: 20, viewBox: '0 0 24 24',
        attrs: { fill: 'currentColor' },
        children: [
            { d: 'M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H5.17L4 17.17V4h16v12z' },
            { d: 'M7 9h2v2H7zm4 0h2v2h-2zm4 0h2v2h-2z' }
        ]
    },
    whatsapp: {
        width: 20, height: 20, viewBox: '0 0 24 24',
        attrs: { fill: 'currentColor' },
        children: [
            { d: 'M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z' }
        ]
    },
    email: {
        width: 20, height: 20, viewBox: '0 0 24 24',
        attrs: { fill: 'none', stroke: 'currentColor', 'stroke-width': '2' },
        children: [
            { d: 'M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z' },
            { tag: 'polyline', points: '22,6 12,13 2,6' }
        ]
    },
    twitter: {
        width: 18, height: 18, viewBox: '0 0 24 24',
        attrs: { fill: 'currentColor' },
        children: [
            { d: 'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z' }
        ]
    },
    facebook: {
        width: 20, height: 20, viewBox: '0 0 24 24',
        attrs: { fill: 'currentColor' },
        children: [
            { d: 'M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z' }
        ]
    },
    linkedin: {
        width: 20, height: 20, viewBox: '0 0 24 24',
        attrs: { fill: 'currentColor' },
        children: [
            { d: 'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z' }
        ]
    },
    reddit: {
        width: 20, height: 20, viewBox: '0 0 24 24',
        attrs: { fill: 'currentColor' },
        children: [
            { d: 'M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z' }
        ]
    },
    telegram: {
        width: 20, height: 20, viewBox: '0 0 24 24',
        attrs: { fill: 'currentColor' },
        children: [
            { d: 'M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z' }
        ]
    },
    copy: {
        width: 16, height: 16, viewBox: '0 0 24 24',
        attrs: { fill: 'none', stroke: 'currentColor', 'stroke-width': '2' },
        children: [
            { tag: 'rect', x: '9', y: '9', width: '13', height: '13', rx: '2', ry: '2' },
            { d: 'M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1' }
        ]
    },
    link: {
        width: 16, height: 16, viewBox: '0 0 24 24',
        attrs: { fill: 'none', stroke: 'currentColor', 'stroke-width': '2' },
        children: [
            { d: 'M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71' },
            { d: 'M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71' }
        ]
    }
};

// Share target button configurations
const SHARE_TARGET_CONFIG = [
    { id: 'sms', name: 'Message', icon: 'sms' },
    { id: 'whatsapp', name: 'WhatsApp', icon: 'whatsapp' },
    { id: 'email', name: 'Email', icon: 'email' },
    { id: 'twitter', name: 'X', icon: 'twitter' },
    { id: 'facebook', name: 'Facebook', icon: 'facebook' },
    { id: 'linkedin', name: 'LinkedIn', icon: 'linkedin' },
    { id: 'reddit', name: 'Reddit', icon: 'reddit' },
    { id: 'telegram', name: 'Telegram', icon: 'telegram' }
];

/**
 * Create an SVG element from icon config
 * @param {string} iconName - Key from SHARE_ICONS
 * @param {string} className - Optional class for the SVG
 * @returns {SVGElement}
 */
function createIcon(iconName, className = '') {
    const icon = SHARE_ICONS[iconName];
    if (!icon) return null;

    const svg = createSvg(icon.width, icon.height, icon.viewBox, icon.children, icon.attrs);
    if (className) svg.classList.add(className);
    return svg;
}

/**
 * Create a share target button
 * @param {Object} config - {id, name, icon}
 * @returns {HTMLButtonElement}
 */
function createShareTargetButton(config) {
    const btn = createElement('button', '', {
        className: 'share-target',
        'aria-label': `Share ${config.id === 'sms' ? 'via' : 'on'} ${config.name}`,
        dataset: { shareTarget: config.id }
    });

    const iconSpan = createElement('span', '', { className: 'share-target-icon' });
    iconSpan.appendChild(createIcon(config.icon));

    const nameSpan = createElement('span', config.name, { className: 'share-target-name' });

    btn.appendChild(iconSpan);
    btn.appendChild(nameSpan);
    return btn;
}

/**
 * Create a condition display item
 * @param {string} label - Condition label
 * @param {string} id - Element ID for the value
 * @returns {HTMLDivElement}
 */
function createConditionItem(label, id) {
    const div = createElement('div', '', { className: 'share-condition' });
    div.appendChild(createElement('span', label, { className: 'share-condition-label' }));
    div.appendChild(createElement('span', '--', { className: 'share-condition-value', id }));
    return div;
}

/**
 * Create the share panel and copy toast DOM elements dynamically
 * Called on first share interaction to reduce initial DOM size
 */
function createSharePanelDOM() {
    // Create share panel container
    const panel = createElement('div', '', { className: 'share-panel', id: 'share-panel' });
    panel.hidden = true;

    // Backdrop
    panel.appendChild(createElement('div', '', { className: 'share-backdrop', id: 'share-backdrop' }));

    // Content container
    const content = createElement('div', '', { className: 'share-content' });

    // Header
    const header = createElement('div', '', { className: 'share-header' });
    header.appendChild(createElement('span', 'Share Conditions', { className: 'share-title', id: 'share-title' }));

    const closeBtn = createElement('button', '', { className: 'share-close', id: 'share-close', 'aria-label': 'Close share panel' });
    closeBtn.appendChild(createIcon('close'));
    header.appendChild(closeBtn);
    content.appendChild(header);

    // Preview card
    const previewCard = createElement('div', '', { className: 'share-preview-card', id: 'share-preview-card' });

    // Preview header with location
    const previewHeader = createElement('div', '', { className: 'share-preview-header' });
    const locationDiv = createElement('div', '', { className: 'share-location' });
    const locationIcon = createIcon('location', 'location-icon');
    locationDiv.appendChild(locationIcon);
    locationDiv.appendChild(createElement('span', 'Location', { className: 'share-location-name', id: 'share-location-name' }));
    previewHeader.appendChild(locationDiv);
    previewCard.appendChild(previewHeader);

    // Rating display
    const ratingDisplay = createElement('div', '', { className: 'share-rating-display' });

    const ratingBadge = createElement('div', '', { className: 'share-rating-badge', id: 'share-rating-badge' });
    ratingBadge.appendChild(createElement('span', '--', { className: 'share-rating-text', id: 'share-rating-text' }));
    ratingDisplay.appendChild(ratingBadge);

    const scoreDiv = createElement('div', '', { className: 'share-score', id: 'share-score' });
    scoreDiv.appendChild(createElement('span', '--', { className: 'share-score-value' }));
    scoreDiv.appendChild(createElement('span', '/100', { className: 'share-score-label' }));
    ratingDisplay.appendChild(scoreDiv);

    previewCard.appendChild(ratingDisplay);

    // Conditions grid
    const conditionsGrid = createElement('div', '', { className: 'share-conditions-grid' });
    conditionsGrid.appendChild(createConditionItem('Swell', 'share-swell'));
    conditionsGrid.appendChild(createConditionItem('Wind', 'share-wind'));
    conditionsGrid.appendChild(createConditionItem('Tide', 'share-tide'));
    conditionsGrid.appendChild(createConditionItem('Water', 'share-water'));
    previewCard.appendChild(conditionsGrid);

    content.appendChild(previewCard);

    // URL preview
    const urlPreview = createElement('div', '', { className: 'share-url-preview' });
    urlPreview.appendChild(createElement('p', '', { className: 'share-preview-url', id: 'share-preview-url' }));
    content.appendChild(urlPreview);

    // Hidden text preview (legacy)
    const textPreview = createElement('p', '', { className: 'share-preview-text', id: 'share-preview-text' });
    textPreview.hidden = true;
    content.appendChild(textPreview);

    // Share targets grid
    const targetsDiv = createElement('div', '', { className: 'share-targets' });
    for (const config of SHARE_TARGET_CONFIG) {
        targetsDiv.appendChild(createShareTargetButton(config));
    }
    content.appendChild(targetsDiv);

    // Actions (copy buttons)
    const actionsDiv = createElement('div', '', { className: 'share-actions' });

    const copyAllBtn = createElement('button', '', { className: 'share-copy-btn', id: 'share-copy-btn' });
    copyAllBtn.appendChild(createIcon('copy'));
    copyAllBtn.appendChild(document.createTextNode(' Copy All'));

    const copyUrlBtn = createElement('button', '', { className: 'share-copy-url-btn', id: 'share-copy-url-btn' });
    copyUrlBtn.appendChild(createIcon('link'));
    copyUrlBtn.appendChild(document.createTextNode(' Copy Link'));

    actionsDiv.appendChild(copyAllBtn);
    actionsDiv.appendChild(copyUrlBtn);
    content.appendChild(actionsDiv);

    panel.appendChild(content);
    document.body.appendChild(panel);

    // Create copy toast
    const toast = createElement('div', '', { className: 'copy-toast', id: 'copy-toast', 'aria-live': 'polite' });
    document.body.appendChild(toast);
}

/**
 * Initialize share functionality
 * Sets up all event listeners for share components
 */
export function initShare() {
    // Create share panel DOM elements dynamically (reduces initial DOM size)
    createSharePanelDOM();

    // Share button in hero card
    const shareBtn = document.getElementById('share-btn');
    if (shareBtn) {
        shareBtn.addEventListener('click', handleShareClick);
    }

    // Share panel close handlers
    const panelBackdrop = document.getElementById('share-backdrop');
    const panelClose = document.getElementById('share-close');

    if (panelBackdrop) {
        panelBackdrop.addEventListener('click', closeSharePanel);
    }
    if (panelClose) {
        panelClose.addEventListener('click', closeSharePanel);
    }

    // Escape key to close panel
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && isSharePanelOpen) {
            closeSharePanel();
        }
    });

    // Share target buttons
    document.querySelectorAll('[data-share-target]').forEach(btn => {
        btn.addEventListener('click', () => {
            shareToTarget(btn.dataset.shareTarget);
        });
    });

    // Copy buttons
    const copyBtn = document.getElementById('share-copy-btn');
    if (copyBtn) {
        copyBtn.addEventListener('click', async () => {
            const success = await copyToClipboard();
            showCopyFeedback(success);
        });
    }

    const copyUrlBtn = document.getElementById('share-copy-url-btn');
    if (copyUrlBtn) {
        copyUrlBtn.addEventListener('click', async () => {
            const success = await copyUrlToClipboard();
            showCopyFeedback(success);
        });
    }
}
